import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { branding } from '@/config/branding';
import { useConversations } from '@/hooks/conversation/useConversations';
import { useMessageStreamContext } from '@/contexts/MessageStreamContext';
import { useConversationsContext } from '@/contexts/ConversationsContext';
import { Message } from '@/types';
import { navigateTo } from '@/lib/hashRouter';
import {
  useUIStore,
  useProjectsStore,
  useFilesStore,
  useAuthStore,
} from '@/stores';
import ChatInput from '@/components/ChatInput';
import ConfirmDialog from '@/components/ConfirmDialog';
import { useGroupChatHandler } from '@/hooks/chat/useGroupChatHandler';
import { useChatScroll } from '@/hooks/chat/useChatScroll';
import { useChatActions } from '@/hooks/chat/useChatActions';
import { useMessageHandler } from '@/hooks/chat/useMessageHandler';
import { useRetryAndEditMessage } from '@/hooks/chat/useRetryAndEditMessage';
import { useMessageOperations } from '@/hooks/chat/useMessageOperations';
import { useSkillTaskStore } from '@/stores/skillTaskStore';
import { useSharedConversation } from '@/hooks/chat/useSharedConversation';
import { useProjectDetailHandler } from '@/hooks/chat/useProjectDetailHandler';
import { ChatMessagesView } from './ChatMessagesView';
import { ChatWelcomeView } from './ChatWelcomeView';
import { ChatHeader } from './ChatHeader';
import ProjectFileList from './ProjectFileList';
import FilePreview from '@/components/FilePreview';
import { X, FolderOpen } from 'lucide-react';
import GroupMemberList from '@/components/GroupMemberList';
import MobileHeader from '@/components/MobileHeader';
import { ChevronLeft } from 'lucide-react';
interface ChatPageProps {
  projectId?: string | null;
  projectName?: string | null;
  shareConversationId?: string;
  selectedConversationId?: string;
  sharedConversationRecordId?: number | null;
  readOnly?: boolean;
  onBackToShared?: () => void;
  stopRequestRef?: React.MutableRefObject<(() => void) | null>;
  usePublic: boolean;
  allProjects: boolean;
  onProjectClick?: (projectId: string) => void;
}
export default function ChatPage({
  projectId = null,
  projectName = null,
  shareConversationId,
  selectedConversationId,
  sharedConversationRecordId = null,
  readOnly = false,
  onBackToShared,
  stopRequestRef,
  onProjectClick,
}: ChatPageProps) {
  const { t } = useTranslation();
  const {
    selectedAgent,
    newChatAgent,
    setConversationAgent,
    setNewChatAgent,
    setNewChatProject,
    isWaitingForMessageInit,
    pendingTasks,
    // 🔥 新增：文件预览单例模式
    filePreview,
    setFilePreview,
    // 🔥 Artifact 分屏渲染
    setArtifactViewer,
  } = useUIStore();
  const { setSidebarProjectConversationsCache } = useConversationsContext();
  const projects = useProjectsStore(state => state.projects);
  const {
    conversations,
    currentConversation,
    currentConversationId,
    pendingConversationId,
    setCurrentConversationId,
    loadConversations,
    loadConversationDetails,
    selectConversation,
    updateConversationMessage,
    deleteConversationSession,
    setConversations,
    addMessageToConversation,
  } = useConversations(projectId);

  const isSharedRecordView = sharedConversationRecordId !== null;

  // File list state
  const [showFileList, setShowFileList] = useState(false);

  // 🔥 当前对话的收藏状态
  const [isConversationStarred, setIsConversationStarred] = useState(false);

  // 🔥 同步当前对话的收藏状态
  useEffect(() => {
    setIsConversationStarred(currentConversation?.starred ?? false);
  }, [currentConversation?.starred]);

  // 🔥 Expose function to update starred state (for Sidebar sync)
  useEffect(() => {
    window.setCurrentConversationStarred = (
      conversationId: string,
      starred: boolean
    ) => {
      const currentId =
        currentConversation?.conversationId || currentConversation?.id;
      // 🔥 使用 String() 统一类型，避免数字 vs 字符串比较失败
      if (String(currentId) === String(conversationId)) {
        setIsConversationStarred(starred);
      }
    };
    return () => {
      delete window.setCurrentConversationStarred;
    };
  }, [currentConversation?.conversationId, currentConversation?.id]);

  // 🔥 文件预览状态已移至 uiStore（单例模式）
  // 删除本地状态：previewFile, previewFileUrl
  // 现在使用 filePreview 和 setFilePreview 从 store

  // Get file loading functions from store
  const loadProjectFilesFromStore = useFilesStore(
    state => state.loadProjectFiles
  );
  const projectFilesCache = useFilesStore(state => state.projectFilesCache);

  // Compute actual project info: prioritize current conversation's project over prop
  const actualProjectId = useMemo(() => {
    if (currentConversation) {
      return currentConversation.projectId ?? null;
    }
    return projectId;
  }, [currentConversation, projectId]);

  const actualProjectName = useMemo(() => {
    if (actualProjectId && projects) {
      const project = projects.find(p => p.id === actualProjectId);
      return project?.name || projectName;
    }
    return projectName;
  }, [actualProjectId, projects, projectName]);

  // 🔥 获取项目角色
  const actualProjectRole = useMemo(() => {
    if (actualProjectId && projects) {
      const project = projects.find(p => p.id === actualProjectId);
      return project?.role || null;
    }
    return null;
  }, [actualProjectId, projects]);

  // Load project files when file list is shown
  useEffect(() => {
    if (showFileList && actualProjectId) {
      loadProjectFilesFromStore(actualProjectId);
    }
  }, [showFileList, actualProjectId, loadProjectFilesFromStore]);

  // Get files from cache
  const projectFiles = actualProjectId
    ? projectFilesCache[actualProjectId]?.fileTree || []
    : [];
  const isLoadingFiles = actualProjectId
    ? projectFilesCache[actualProjectId]?.isLoading
    : false;

  // 🔥 打开文件预览（单例模式 - 使用 store）
  const openFilePreview = useCallback(
    (node: any) => {
      if (!actualProjectId) return;
      const fileUrl = `${window.location.origin}/api/projects/${actualProjectId}/files/content?filePath=${encodeURIComponent(node.path || node.name)}&Authorization=${localStorage.getItem('user-token')}&asAttachment=false`;
      // 🔥 打开文件预览时关闭 Artifact 分屏（互斥）
      setArtifactViewer(null);
      // 🔥 使用 store 的 setFilePreview，实现单例模式
      // 如果预览已打开，点击其他文件只是替换内容，不会重新打开
      setFilePreview({
        fileUrl,
        filename: node.name,
        projectId: actualProjectId,
        filePath: node.path || node.name,
      });
    },
    [actualProjectId, setFilePreview, setArtifactViewer]
  );

  // 🔥 关闭文件预览已由 FilePreview 组件内部的 handleClose 处理
  // 它会调用 setFilePreview(null) 清空 store 状态

  // 计算当前对话的 agent 信息（从消息中获取）
  const currentConversationAgent = useMemo(() => {
    if (!currentConversation?.messages) return null;
    // 从最后一条有 client 信息的 assistant 消息中获取 agent
    for (let i = currentConversation.messages.length - 1; i >= 0; i--) {
      const msg = currentConversation.messages[i];
      if (msg.role === 'assistant' && msg.client) {
        return {
          id: msg.client.id,
          name: msg.client.name,
          logo: msg.client.logo,
        };
      }
    }
    return null;
  }, [currentConversation?.messages]);

  const loadProjectFiles = useFilesStore(state => state.loadProjectFiles);
  const resetProjectFilesCache = useFilesStore(
    state => state.resetProjectFilesCache
  );

  // Load project files when entering a conversation with projectId
  useEffect(() => {
    if (actualProjectId) {
      loadProjectFiles(actualProjectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualProjectId]);

  // 删除确认对话框状态
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    isOpen: boolean;
    message?: Message;
  }>({ isOpen: false });
  const [isDeleting, setIsDeleting] = useState(false);
  // 🔥 群聊回复状态
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  // 🔥 使用 useChatScroll hook 处理滚动逻辑
  const {
    messagesEndRef,
    scrollContainerRef,
    showScrollButton,
    scrollToBottom,
  } = useChatScroll({ messages: currentConversation?.messages });
  const currentConversationIdRef = useRef<string | undefined>(
    currentConversationId
  );
  // 跟踪已处理的 selectedConversationId，避免重复调用
  const processedSelectedIdRef = useRef<string | undefined>(undefined);
  // 跟踪上一次加载 conversations 的 projectId
  const loadedProjectIdRef = useRef<string | null | undefined>(undefined);
  // 使用 ref 存储最新的函数引用和状态，避免闭包问题
  const updateConversationMessageRef = useRef(updateConversationMessage);
  const currentConversationIdRefForCallback = useRef(currentConversationId);
  const conversationsRef = useRef(conversations);
  const setCurrentConversationIdRef = useRef(setCurrentConversationId);
  const setConversationsRef = useRef(setConversations);
  const setSidebarProjectConversationsCacheRef = useRef(
    setSidebarProjectConversationsCache
  );
  // 🔥 群聊：获取当前用户 ID，用于区分自己的消息和其他用户的消息
  const currentUserInfo = useAuthStore(state => state.userInfo);
  const currentUserIdRef = useRef(currentUserInfo?.id);
  const updateConversationLoadingStateRef = useRef<
    ((conversationId: string, isLoading: boolean) => void) | null
  >(null);
  const loadConversationDetailsRef = useRef<
    ((conversationId: string, force?: boolean) => void) | null
  >(null);
  // 🔥 scrollToBottom 已由 useChatScroll hook 提供

  // 🔥 滚动监听已由 useChatScroll hook 内部处理

  // 更新 refs
  // 合并的 ref 更新 - 保持 ref 与最新值同步
  useEffect(() => {
    updateConversationMessageRef.current = updateConversationMessage;
  }, [updateConversationMessage]);
  useEffect(() => {
    currentConversationIdRefForCallback.current = currentConversationId;
  }, [currentConversationId]);
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);
  useEffect(() => {
    setCurrentConversationIdRef.current = setCurrentConversationId;
  }, [setCurrentConversationId]);
  useEffect(() => {
    setConversationsRef.current = setConversations;
  }, [setConversations]);
  useEffect(() => {
    setSidebarProjectConversationsCacheRef.current =
      setSidebarProjectConversationsCache;
  }, [setSidebarProjectConversationsCache]);
  useEffect(() => {
    currentUserIdRef.current = currentUserInfo?.id;
  }, [currentUserInfo?.id]);
  // 辅助函数：同时更新 conversations 和 sidebar cache 的 isLoading 状态
  // 使用 useCallback 但通过 ref 访问最新函数，避免依赖变化
  const updateConversationLoadingState = useCallback(
    (conversationId: string, isLoading: boolean) => {
      // 更新全局 conversations store - 通过 ref 访问最新函数
      setConversationsRef.current(prev =>
        prev.map(conversation =>
          conversation.id === conversationId
            ? { ...conversation, isLoading }
            : conversation
        )
      );

      // 🔥 修复：同时更新 sidebar cache 的 isLoading 状态
      // 之前只在 isLoading=false 时更新，导致历史对话开始流式时侧边栏没有 loading
      setSidebarProjectConversationsCacheRef.current(prev => {
        const newCache = { ...prev };
        let updated = false;
        // 遍历所有 projects，找到包含该 conversation 的 project
        Object.keys(newCache).forEach(pid => {
          const projectConvs = newCache[pid] || [];
          const hasConv = projectConvs.some(conv => conv.id === conversationId);
          if (hasConv) {
            newCache[pid] = projectConvs.map(conversation =>
              conversation.id === conversationId
                ? { ...conversation, isLoading }
                : conversation
            );
            updated = true;
          }
        });
        return updated ? newCache : prev;
      });
    },
    [] // 空依赖，通过 ref 访问最新函数
  );
  // 更新 updateConversationLoadingState ref
  useEffect(() => {
    updateConversationLoadingStateRef.current = updateConversationLoadingState;
  }, [updateConversationLoadingState]);
  // 更新 loadConversationDetails ref
  useEffect(() => {
    loadConversationDetailsRef.current = loadConversationDetails;
  }, [loadConversationDetails]);
  // Handle incoming messages - 使用 useCallback 并通过 ref 访问最新状态
  // IMPORTANT: 空依赖数组确保这个函数永远不会重新创建
  // 🔥 使用 useMessageHandler hook 处理 SSE 消息
  // 替代原有的 handleIncomingMessage 函数（约 1500 行）
  const { handleIncomingMessage } = useMessageHandler({
    conversationsRef,
    setConversations,
    setSidebarProjectConversationsCache,
    currentConversationIdRef,
    setCurrentConversationId,
    updateConversationMessage,
    updateConversationLoadingState,
    loadConversationDetails,
  });
  // 使用全局的 MessageStream 上下文
  const {
    sendMessage,
    stopRequest,
    registerMessageHandler,
    unregisterMessageHandler,
  } = useMessageStreamContext();
  // 注册/注销消息处理器（只在组件挂载/卸载时，不受 projectId 影响）
  // IMPORTANT: 消息处理器应该是全局的，处理所有消息
  // 消息路由通过 conversationId 直接定位对话
  useEffect(() => {
    const handlerId = 'chatpage-global';
    registerMessageHandler(handlerId, handleIncomingMessage);
    return () => {
      unregisterMessageHandler(handlerId);
    };
  }, [registerMessageHandler, unregisterMessageHandler, handleIncomingMessage]);

  // ProjectDetail 消息处理 hook
  useProjectDetailHandler({
    conversationsRef,
    setConversations,
    projectId,
    sendMessage,
    updateConversationMessage,
    updateConversationLoadingState,
  });

  // 🔥 群聊 WebSocket 消息处理
  // 检测当前对话是否为群聊
  // 🔥 修复：同时检查 window.groupChatIds 和 conversation.conversationType
  // 这样刷新浏览器时，即使 sidebar 还没加载完成，API 返回后也能识别群聊
  const isCurrentGroupChat = useMemo(() => {
    if (!currentConversationId) return false;
    // 检查 window.groupChatIds（sidebar 加载的）或 conversation.conversationType（API 返回的）
    return (
      window.groupChatIds?.has(currentConversationId) ||
      currentConversation?.conversationType === 'group'
    );
  }, [currentConversationId, currentConversation?.conversationType]);

  // 🔥 检测当前会话是否为 skill 会话（使用群聊 API 但不标记为群聊）
  const activeSkillConversations = useUIStore(
    state => state.activeSkillConversations
  );
  const isCurrentSkillConversation = useMemo(() => {
    if (!currentConversationId) return false;
    return !!activeSkillConversations[currentConversationId];
  }, [currentConversationId, activeSkillConversations]);

  // 🔥 不再需要 shouldUseWebSocket 变量
  // skill 会话有自己的 WS handler（useMessageOperations.ts 中的 socketService.on('chat_message', ...)）
  // 群聊使用 useGroupChatHandler hook

  // 🔥 计算当前会话的任务错误状态（用于禁用输入框并显示错误信息）
  const currentTaskError = useMemo(() => {
    if (!currentConversationId || !pendingTasks.length) return null;
    // 查找当前会话中有 errorMessage 的任务（即状态为 'error' 的任务）
    const errorTask = pendingTasks.find(
      task => task.conversationId === currentConversationId && task.errorMessage
    );
    return errorTask?.errorMessage || null;
  }, [currentConversationId, pendingTasks]);

  // 🔥 清除当前会话的任务错误（从队列中移除错误任务）
  const handleClearTaskError = useCallback(() => {
    if (!currentConversationId) return;
    const errorTask = pendingTasks.find(
      task => task.conversationId === currentConversationId && task.errorMessage
    );
    if (errorTask) {
      useUIStore.getState().removeTask(errorTask.id);
    }
  }, [currentConversationId, pendingTasks]);

  // 🔥 当任务出错时，清除侧边栏的 loading 状态
  useEffect(() => {
    if (currentTaskError && currentConversationId) {
      updateConversationLoadingState(currentConversationId, false);
    }
  }, [currentTaskError, currentConversationId, updateConversationLoadingState]);

  // 🔥 切换到非 skill 对话时，关闭 Tasks Status 面板
  useEffect(() => {
    if (!isCurrentSkillConversation) {
      useSkillTaskStore.getState().setDashboardOpen(false);
    }
  }, [currentConversationId, isCurrentSkillConversation]);

  // 🔥 切换对话时，尝试加载 board 数据（适用于首次进入 skill 对话或刷新浏览器后恢复）
  // 不再依赖 isCurrentSkillConversation（刷新后内存状态丢失），而是直接尝试 fetchBoard
  // 如果 API 返回了 board 数据，fetchBoard 会自动注册为 skill 对话
  useEffect(() => {
    if (!currentConversationId) return;
    // temp- ID 是前端临时 ID，后端不认识，跳过
    if (currentConversationId.startsWith('temp-')) return;
    // 只在 store 中没有该对话的 board 数据时才调用 fetchBoard
    const existingBoard = useSkillTaskStore
      .getState()
      .getBoard(currentConversationId);
    if (!existingBoard) {
      useSkillTaskStore.getState().fetchBoard(currentConversationId);
    }
  }, [currentConversationId]);

  // 🔥 群聊消息处理已移至 useGroupChatHandler hook
  // 以下 handleGroupChat* 函数已被移除，由 useGroupChatHandler 内部处理

  // 🔥 保留此注释作为占位，标记原有代码位置
  // 原有代码：handleGroupChatUserMessage, handleGroupChatSelfMessage,
  // handleGroupChatMessageInit, handleGroupChatContent, handleGroupChatThink, handleGroupChatHashCode
  // 🔥 使用 useGroupChatHandler hook 处理群聊 WebSocket 消息
  // 替代原有的 handleGroupChat* 函数（已移至 useGroupChatHandler）
  useGroupChatHandler({
    conversationId: currentConversationId,
    isGroupChat: isCurrentGroupChat, // 🔥 只有真正的群聊使用 GroupChatHandler，skill 会话有自己的 handler（useMessageOperations.ts）
    conversationsRef,
    setConversations,
    addMessageToConversation,
    updateConversationLoadingState,
    scrollToBottom,
  });

  // 🔥 使用 useChatActions hook 处理用户操作
  // 替代原有的 handleStopRequest, handleStopGroupChat, confirmDelete, handleChangeChoice 等函数
  const {
    handleStopRequest,
    handleStopGroupChat,
    handleChangeChoiceForTree,
    handleChangeChoice,
    confirmDeleteMessage,
  } = useChatActions({
    currentConversationId,
    currentConversation: currentConversation ?? undefined,
    conversationsRef,
    setConversations,
    setSidebarProjectConversationsCache,
    updateConversationLoadingState,
    stopRequest,
    sendMessage,
    deleteConversationSession,
    loadConversations,
    loadConversationDetails,
    selectedAgent,
    currentConversationAgent,
  });

  // 消息重试和编辑 hook
  const { handleRetry, handleEditSave, setHandleSendMessage } =
    useRetryAndEditMessage({
      currentConversation: currentConversation ?? undefined,
      currentConversationId,
      setConversations,
      currentConversationAgent,
      selectedAgent,
    });

  // 消息发送操作 hook
  const { handleSendMessage, handleCancelMessage } = useMessageOperations({
    currentConversationId,
    currentConversation: currentConversation ?? undefined,
    conversationsRef,
    setConversations,
    setCurrentConversationId,
    setSidebarProjectConversationsCache,
    sendMessage,
    updateConversationLoadingState,
    selectedAgent,
    newChatAgent,
    setConversationAgent,
    setNewChatAgent,
    projectId: actualProjectId,
    currentConversationIdRef,
    currentConversationIdRefForCallback,
  });

  // 删除消息处理函数
  const handleDelete = (message: Message) => {
    setDeleteConfirmDialog({ isOpen: true, message });
  };

  const confirmDelete = async () => {
    if (deleteConfirmDialog.message) {
      setIsDeleting(true);
      try {
        await confirmDeleteMessage(deleteConfirmDialog.message);
      } finally {
        setIsDeleting(false);
        setDeleteConfirmDialog({ isOpen: false });
      }
    }
  };

  // 存储stopRequest函数到父组件传递的ref
  useEffect(() => {
    if (stopRequestRef) {
      stopRequestRef.current = stopRequest;
    }
  }, [stopRequest, stopRequestRef]);
  // Sync ref with state
  useEffect(() => {
    currentConversationIdRef.current = currentConversationId;
    currentConversationIdRefForCallback.current = currentConversationId;
  }, [currentConversationId]);
  // Load conversations on mount
  useEffect(() => {
    if (!isSharedRecordView) {
      shareConversationId && loadConversations(shareConversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareConversationId, isSharedRecordView]); // Only depend on shareConversationId, not loadConversations
  // Load project conversations when projectId changes
  useEffect(() => {
    // 只在 projectId 真正变化时加载项目会话
    // 避免在视图切换时重复加载（例如从 ProjectDetail 切换到 ChatPage）
    if (
      projectId &&
      !shareConversationId &&
      !isSharedRecordView &&
      loadedProjectIdRef.current !== projectId
    ) {
      loadedProjectIdRef.current = projectId;
      // 延迟检查，避免在组件刚挂载时立即执行
      setTimeout(() => {
        // 简单检查：useConversations hook 会自动处理重复加载的情况
        // 所以这里可以放心调用 loadConversations
        loadConversations();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]); // Only depend on projectId
  // Respond to external conversation selection (from Sidebar or ProjectDetail)
  // 🔥 优化：selectConversation 内部会检查 alreadySelected，跳过重复的状态更新
  // 它还有正确的缓存逻辑：只有当 conversation 没有 messages 时才加载详情
  useEffect(() => {
    if (isSharedRecordView) return;
    // 如果 selectedConversationId 变为 undefined，重置 processedSelectedIdRef
    if (!selectedConversationId) {
      processedSelectedIdRef.current = undefined;
      return;
    }
    // 只有当 selectedConversationId 变化时才处理（避免重复调用）
    if (
      selectedConversationId &&
      selectedConversationId !== processedSelectedIdRef.current
    ) {
      processedSelectedIdRef.current = selectedConversationId;
      // 🔥 切换对话时关闭文件预览和 Artifact 分屏
      setFilePreview(null);
      setArtifactViewer(null);
      // selectConversation 会：
      // 1. 检查 alreadySelected，跳过重复的 setCurrentConversationId
      // 2. 检查 conversation.messages，如果已有消息则跳过 API 请求
      selectConversation(selectedConversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversationId, selectConversation]);
  // Use ref to track current projectId without causing re-registration
  const projectIdRef = useRef(projectId);
  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);
  // Update sendMessage ref
  const sendMessageRef = useRef(sendMessage);
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  // 🔥 监听 sendNextPendingTask 事件，在收到 message_init 后发送下一个等待任务
  useEffect(() => {
    const handleSendNextPendingTask = async (event: Event) => {
      const { task, previousMessageId } = (event as CustomEvent).detail;
      if (!task) return;

      // 🔥 区分 chat 和 cancel 任务
      if (task.type === 'cancel') {
        // ===== 处理 cancel 任务 =====
        try {
          // 更新任务状态为 'sent'
          useUIStore.getState().updateTaskStatus(task.id, 'sent');

          // 获取 targetUserMessageId
          const targetUserMessageId = task.targetUserMessageId;
          if (!targetUserMessageId) {
            console.error('❌ Cancel 任务缺少 targetUserMessageId', task);
            useUIStore.getState().removeTask(task.id);
            return;
          }

          // 🔥 修复：在执行时获取最新的 assistantMessageId，而不是使用插入时记录的旧值
          // 这样可以处理在 cancel 任务排队期间有新消息到达的情况
          const latestInit = useUIStore
            .getState()
            .getLatestMessageInit(task.conversationId);
          const cancelPreviousMessageId =
            latestInit?.assistantMessageId ||
            task.previousMessageId ||
            previousMessageId ||
            0;

          console.log('🛑 Cancel 任务执行 - 获取最新 previousMessageId', {
            taskConversationId: task.conversationId,
            latestAssistantMessageId: latestInit?.assistantMessageId,
            taskPreviousMessageId: task.previousMessageId,
            eventPreviousMessageId: previousMessageId,
            finalPreviousMessageId: cancelPreviousMessageId,
          });

          // 调用 cancelMessage API
          const { cancelMessage } =
            await import('@/services/conversationService');
          const result = await cancelMessage(
            targetUserMessageId,
            cancelPreviousMessageId
          );

          console.log('🛑 Cancel 任务执行成功', result);

          // 🔥 创建 cancel 节点并更新 treeData
          // 🔥 修改：构建两个 type: 'cancel' 节点，模拟一轮完整对话
          // - cancelUserNode: role='user', id=messageId
          // - cancelAssistantNode: role='assistant', id=assistantMessageId (作为 user 节点的 children)
          const conversation = conversationsRef.current.find(
            c => c.id === task.conversationId
          );
          if (conversation?.treeData) {
            // 创建 assistant cancel 节点（作为 user 节点的子节点）
            const cancelAssistantNode = {
              id: result.assistantMessageId,
              type: 'cancel',
              role: 'assistant' as const,
              contents: [
                {
                  type: 'text',
                  content: result.content || 'Message canceled',
                  createdAt: new Date().toISOString(),
                },
              ],
              isStreaming: false,
              messageStatus: 'canceled' as const,
              previousMessageId: result.messageId, // 父节点是 user cancel 节点
              backendId: result.assistantMessageId,
              client: result.client,
              children: [] as any[],
            };

            // 创建 user cancel 节点
            const cancelUserNode = {
              id: result.messageId,
              type: 'cancel',
              role: 'user' as const,
              contents: [
                {
                  type: 'text',
                  content: 'Canceled', // user 节点的内容
                  createdAt: new Date().toISOString(),
                },
              ],
              isStreaming: false,
              messageStatus: 'canceled' as const,
              previousMessageId: result.previousMessageId,
              backendId: result.messageId,
              children: [cancelAssistantNode] as any[], // assistant 节点作为子节点
              // 🔥 标记被取消的消息 ID
              cancelledMessageId: targetUserMessageId,
            };

            // 🔥 记录 cancel 节点添加的位置
            let cancelNodeAddedAt: { depth: number; index: number } | null =
              null;

            // 在 treeData 中找到 previousMessageId 对应的节点，添加 cancel 子节点
            const addCancelNode = (nodes: any[], depth: number = 0): any[] => {
              return nodes.map(node => {
                if (
                  node.id === result.previousMessageId ||
                  node.backendId === result.previousMessageId
                ) {
                  const newChildren = [
                    ...(node.children || []),
                    cancelUserNode,
                  ];
                  cancelNodeAddedAt = {
                    depth: depth + 1,
                    index: newChildren.length - 1,
                  };
                  return { ...node, children: newChildren };
                }
                if (node.children?.length > 0) {
                  return {
                    ...node,
                    children: addCancelNode(node.children, depth + 1),
                  };
                }
                return node;
              });
            };

            const updatedTreeData = addCancelNode(conversation.treeData);

            // 🔥 新增：处理已取消的消息 - 标记被取消消息的 isCanceled 属性
            // 🔥 修复：同时标记 user 消息和 assistant 消息
            // - user 消息（targetUserMessageId）：用于在 UI 上显示 "canceled" 状态
            // - assistant 消息（cancelPreviousMessageId）：标记被中断的响应
            const markCancelledMessage = (nodes: any[]): any[] => {
              return nodes.map(node => {
                let shouldMark = false;

                // 检查是否是被取消的 assistant 消息
                if (
                  node.id === cancelPreviousMessageId ||
                  node.backendId === cancelPreviousMessageId
                ) {
                  shouldMark = true;
                }

                // 🔥 新增：检查是否是对应的 user 消息
                // user 节点的 ID 格式可能是 `user-${pendingId}` 或直接是 backendId
                if (
                  node.role === 'user' &&
                  (node.id === targetUserMessageId ||
                    node.backendId === targetUserMessageId ||
                    String(node.id).endsWith(`-${targetUserMessageId}`))
                ) {
                  shouldMark = true;
                }

                if (shouldMark) {
                  return { ...node, isCanceled: true };
                }

                if (node.children?.length > 0) {
                  return {
                    ...node,
                    children: markCancelledMessage(node.children),
                  };
                }
                return node;
              });
            };

            const finalTreeData = markCancelledMessage(updatedTreeData);

            // 更新 versionPath 指向新添加的 cancel 节点
            const newVersionPath: Record<number, number> = {
              ...(conversation.versionPath || {}),
            };
            if (cancelNodeAddedAt !== null) {
              const { depth, index } = cancelNodeAddedAt;
              newVersionPath[depth] = index;
            }

            // 更新 conversations
            setConversations(prev =>
              prev.map(conv =>
                conv.id === task.conversationId
                  ? {
                      ...conv,
                      treeData: finalTreeData,
                      versionPath: newVersionPath,
                    }
                  : conv
              )
            );
          }

          // 移除已完成的 cancel 任务
          useUIStore.getState().removeTask(task.id);

          // 🔥 修复：将被取消的原始任务标记为 'cancelled' 状态
          // 这样 "检测未完成任务" 的逻辑就不会再选中它
          const { taskQueue } = useUIStore.getState();
          const targetTask = taskQueue.find(
            t =>
              t.id === task.targetPendingId ||
              (task.targetUserMessageId &&
                t.userMessageId === task.targetUserMessageId)
          );
          if (targetTask) {
            useUIStore.getState().updateTaskStatus(targetTask.id, 'cancelled');
          }

          // 🔥 修复：更新 latestMessageInit，让后续消息知道最新的 previousMessageId
          // Cancel 返回的节点应该像 message_init 一样被记录，成为下一条消息的 previousMessageId 指向
          useUIStore.getState().setLatestMessageInit({
            conversationId: task.conversationId,
            assistantMessageId: result.assistantMessageId,
            userMessageId: result.messageId,
            pendingId: task.id, // 使用 cancel 任务的 ID
          });

          // 🔥 触发下一个任务（使用 cancel assistant 节点的 ID 作为 previousMessageId）
          const nextTask = useUIStore.getState().getNextPendingTask();
          if (nextTask) {
            // 🔥 修复：检查下一个任务是否属于同一会话
            const isSameConversation =
              nextTask.conversationId === task.conversationId;
            const isNewConversation =
              nextTask.conversationId?.startsWith('temp-') || false;

            // 只有同一会话的任务才使用 cancel 返回的 assistantMessageId
            const nextPreviousMessageId =
              isNewConversation || !isSameConversation
                ? null
                : result.assistantMessageId; // 🔥 修复：使用 assistantMessageId，不是 messageId

            window.dispatchEvent(
              new CustomEvent('sendNextPendingTask', {
                detail: {
                  task: nextTask,
                  previousMessageId: nextPreviousMessageId,
                },
              })
            );
          }
        } catch (error) {
          console.error('❌ Cancel 任务执行失败:', error);
          useUIStore.getState().removeTask(task.id);
        }
      } else {
        // ===== 处理 chat 任务（现有逻辑） =====
        // 更新任务状态为 'sent'
        useUIStore.getState().updateTaskStatus(task.id, 'sent');

        // 🔥 新增：更新消息节点的状态从 'waiting' 到 'pending'
        // 这样 Cancel 按钮的显示逻辑才能正确工作
        setConversations(prev =>
          prev.map(conv => {
            if (conv.id !== task.conversationId || !conv.treeData) return conv;

            const updateMessageStatus = (nodes: any[]): any[] => {
              return nodes.map(node => {
                // 匹配 user 节点 (user-${pendingId}) 或 assistant 节点 (pendingId)
                const isTargetNode =
                  String(node.id) === `user-${task.id}` ||
                  String(node.id) === task.id;

                if (isTargetNode && node.messageStatus === 'waiting') {
                  return {
                    ...node,
                    messageStatus: 'pending',
                  };
                }
                if (node.children?.length > 0) {
                  return {
                    ...node,
                    children: updateMessageStatus(node.children),
                  };
                }
                return node;
              });
            };

            return {
              ...conv,
              treeData: updateMessageStatus(conv.treeData),
            };
          })
        );

        // 发送任务（不经过 handleSendMessage，直接调用 sendMessage）
        try {
          const conversation = conversationsRef.current.find(
            c => c.id === task.conversationId
          );
          const finalProjectId =
            task.projectId || conversation?.projectId || projectId || '';
          const clientId = task.agentId || selectedAgent?.id || null;

          await sendMessage(
            task.content || '',
            task.conversationId,
            task.id, // pendingId
            finalProjectId,
            clientId,
            task.options || {},
            previousMessageId // 使用上一个任务的 assistantMessageId
          );
        } catch (error) {
          console.error('❌ 发送等待任务失败:', error);
          // 移除失败的任务
          useUIStore.getState().removeTask(task.id);
        }
      }
    };

    window.addEventListener('sendNextPendingTask', handleSendNextPendingTask);

    return () => {
      window.removeEventListener(
        'sendNextPendingTask',
        handleSendNextPendingTask
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - register only once on mount, all state access via refs

  // 🔥 新增：监听所有任务完成事件，刷新后端数据
  useEffect(() => {
    const handleAllTasksCompleted = async (event: Event) => {
      const customEvent = event as CustomEvent<{ conversationId: string }>;
      const { conversationId } = customEvent.detail;

      if (!conversationId) {
        console.log('⚠️ allTasksCompleted: 没有 conversationId，跳过刷新');
        return;
      }

      console.log('🔄 allTasksCompleted: 开始刷新后端数据', { conversationId });

      try {
        const { fetchConversationDetails } =
          await import('@/services/conversationService');
        const result = await fetchConversationDetails(conversationId);

        console.log('🔄 allTasksCompleted: 后端数据获取成功', {
          conversationId,
          treeDataLength: result.treeData?.length,
        });

        // 更新 conversations store 中的数据
        setConversationsRef.current(prev =>
          prev.map(conv => {
            if (
              conv.conversationId === conversationId ||
              conv.id === conversationId
            ) {
              return {
                ...conv,
                treeData: result.treeData,
                versionPath: result.versionPath,
                title: result.title || conv.title,
              };
            }
            return conv;
          })
        );

        console.log('🔄 allTasksCompleted: conversations store 已更新');
      } catch (error) {
        console.error('❌ allTasksCompleted: 刷新后端数据失败', error);
      }
    };

    window.addEventListener('allTasksCompleted', handleAllTasksCompleted);

    return () => {
      window.removeEventListener('allTasksCompleted', handleAllTasksCompleted);
    };
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (
      currentConversation &&
      (currentConversation.messages || []).length > 0
    ) {
      // 使用 setTimeout 确保 DOM 完全渲染后再滚动
      const timer = setTimeout(() => {
        if (scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          container.scrollTop = container.scrollHeight;
        }
      }, 0);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentConversation?.messages?.length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    (currentConversation?.messages || [])[
      (currentConversation?.messages || []).length - 1
    ]?.id,
  ]);
  // Handle conversation switching
  useEffect(() => {
    if (
      currentConversation &&
      (currentConversation.messages || []).length > 0
    ) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          container.scrollTop = container.scrollHeight;
        }
      }, 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId]);
  // Handle agent config loading - scroll after agent config is loaded and rendered
  // This ensures the scroll happens after the agent config panel is displayed
  useEffect(() => {
    if (
      currentConversation &&
      (currentConversation.messages || []).length > 0 &&
      selectedAgent
    ) {
      // 使用较长的延迟确保agent配置UI完全渲染
      const timer = setTimeout(() => {
        if (scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          container.scrollTop = container.scrollHeight;
        }
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgent?.id, currentConversationId]);
  /**
   * 创建新对话
   */
  const handleNewConversation = useCallback(() => {
    // 获取要保留的 agent
    // 优先级：用户手动切换的 agent > 从消息中提取的 agent > 全局选中的 agent
    const { conversationAgentMap } = useUIStore.getState();
    const manuallySelectedAgent = currentConversationId
      ? conversationAgentMap[currentConversationId]
      : null;
    const agentToPreserve =
      manuallySelectedAgent || currentConversationAgent || selectedAgent;

    if (agentToPreserve) {
      setNewChatAgent({
        id: agentToPreserve.id,
        name: agentToPreserve.name,
        logo: agentToPreserve.logo,
      });
    }

    // 保留当前项目
    if (actualProjectId && actualProjectName) {
      setNewChatProject({
        id: actualProjectId,
        name: actualProjectName,
      });
    }

    // 清除当前对话选择，这会触发 welcome 视图
    setCurrentConversationId(undefined);
    currentConversationIdRef.current = undefined;
    currentConversationIdRefForCallback.current = undefined;

    // 更新 URL，移除 conversationId（始终导航到聊天页面，项目通过 newChatProject 保留）
    navigateTo({ type: 'chat' });
  }, [
    setCurrentConversationId,
    currentConversationId,
    currentConversationAgent,
    setNewChatAgent,
    setNewChatProject,
    selectedAgent,
    actualProjectId,
    actualProjectName,
  ]);
  /**
   * 发送消息
   */

  // 🔥 连接 handleSendMessage 到 useRetryAndEditMessage hook
  useEffect(() => {
    setHandleSendMessage(handleSendMessage);
  }, [handleSendMessage, setHandleSendMessage]);

  // 🔥 handleStopGroupChat 已移至 useChatActions hook
  // 🔥 handleRetry 已移至 useRetryAndEditMessage hook
  // 🔥 handleEditSave 已移至 useRetryAndEditMessage hook
  /**
   * 处理source点击
   */
  const handleSourceClick = (_sourceId: string, _page?: number) => {
    // TODO: Implement PDF viewer modal
  };
  // 🔥 修复：使用 activeStreamingMessages Map 来判断是否有活跃的流式消息
  // 之前使用 currentConversation?.isLoading，但这个状态在多处被设置为 false，导致 Stop 按钮提前消失
  // 新方案：每个 conversation 用一个 map 记录活跃消息，发送一个添加一个，返回一个移除一个
  const activeStreamingMessages = useUIStore(
    state => state.activeStreamingMessages
  );
  const currentIsLoading =
    (currentConversationId &&
      activeStreamingMessages[currentConversationId]?.length > 0) ||
    false;
  // 🔥 viewer 角色也是只读模式，禁用继续对话和文件编辑功能
  const isReadOnlyMode =
    isSharedRecordView ||
    !!shareConversationId ||
    readOnly ||
    actualProjectRole === 'viewer';

  // 🔥 版本切换是只读操作，viewer 角色也应该能使用
  const canChangeVersionInThisView =
    !currentIsLoading &&
    (!isReadOnlyMode || isSharedRecordView || actualProjectRole === 'viewer');

  // 🔥 监听对话结束，刷新项目文件（AI 可能生成了新文件）
  const prevIsLoadingRef = useRef(currentIsLoading);
  useEffect(() => {
    // 检测 isLoading 从 true 变为 false
    if (prevIsLoadingRef.current && !currentIsLoading) {
      // 如果当前对话有项目，刷新项目文件
      if (actualProjectId) {
        // 先清除缓存，再重新加载
        resetProjectFilesCache(actualProjectId);
        loadProjectFiles(actualProjectId);
      }
    }
    prevIsLoadingRef.current = currentIsLoading;
  }, [
    currentIsLoading,
    actualProjectId,
    loadProjectFiles,
    resetProjectFilesCache,
  ]);

  // 🔥 监听 board_state 的 waiting/end 事件，刷新 Project Files 列表同步后端生成的文件
  useEffect(() => {
    const handleRefreshProjectFiles = (event: Event) => {
      const { conversationId } = (event as CustomEvent).detail;
      // 只刷新当前对话关联的项目文件
      if (!actualProjectId) return;
      const currentConvId = currentConversationId;
      if (conversationId && currentConvId && conversationId !== currentConvId) {
        return; // 不是当前对话的事件，跳过
      }
      console.log(
        '🔄 refreshProjectFiles: board_state waiting/end -> refreshing project files',
        {
          projectId: actualProjectId,
          conversationId,
        }
      );
      resetProjectFilesCache(actualProjectId);
      loadProjectFiles(actualProjectId);
    };

    window.addEventListener('refreshProjectFiles', handleRefreshProjectFiles);
    return () => {
      window.removeEventListener(
        'refreshProjectFiles',
        handleRefreshProjectFiles
      );
    };
  }, [
    actualProjectId,
    currentConversationId,
    resetProjectFilesCache,
    loadProjectFiles,
  ]);

  // 共享对话处理 hook
  const {
    sharedLoadError,
    displayConversation,
    isSwitchingConversation,
    handleChangeChoiceForSharedTree,
    handleChangeChoiceForSharedMessages,
  } = useSharedConversation({
    isSharedRecordView,
    sharedConversationRecordId,
    currentConversation,
    pendingConversationId,
  });

  const showHeader =
    isSharedRecordView ||
    (displayConversation?.messages && displayConversation.messages.length > 0);

  return (
    <div className="flex-1 flex min-w-0 h-full overflow-hidden relative">
      {/* File list sidebar */}
      {showFileList && actualProjectId && (
        <div className="w-80 border-r border-gray-200 bg-gray-50 flex flex-col shadow-sm flex-shrink-0">
          <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
            <h3 className="font-semibold text-gray-800 text-sm">
              Project Files
            </h3>
            <button
              onClick={() => setShowFileList(false)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              title="Close file list"
            >
              <X size={16} className="text-gray-500" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto bg-white">
            {isLoadingFiles ? (
              <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                  Loading files...
                </div>
              </div>
            ) : projectFiles.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
                No files in this project
              </div>
            ) : (
              <ProjectFileList
                projectId={actualProjectId}
                files={projectFiles}
                onSelectFile={openFilePreview}
              />
            )}
          </div>
        </div>
      )}

      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden relative">
        {/* Switching conversation loading overlay */}
        {isSwitchingConversation && (
          <div className="absolute inset-0 bg-claude-bg bg-opacity-80 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-claude-orange"></div>
              <p className="text-gray-600 font-medium">
                Loading conversation...
              </p>
            </div>
          </div>
        )}
        {/* Main content - show current conversation while loading new one */}
        {/* Mobile Header - always visible on mobile, with breadcrumb when showHeader */}
        <MobileHeader>
          {showHeader ? (
            <div className="flex items-center whitespace-nowrap">
              {/* File list toggle button - only show when there's a project */}
              {actualProjectId && !showFileList && (
                <button
                  onClick={() => setShowFileList(true)}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors mr-1"
                  title="View project files"
                >
                  <FolderOpen size={14} />
                  <span>Files</span>
                </button>
              )}
              <ChatHeader
                title={displayConversation?.title || 'New Chat'}
                projectId={actualProjectId}
                projectName={actualProjectName}
                projectRole={actualProjectRole}
                onProjectClick={onProjectClick}
                isSharedRecordView={isSharedRecordView}
                onBackToShared={onBackToShared}
                isSkillConversation={isCurrentSkillConversation}
                conversationId={currentConversationId}
              />
            </div>
          ) : (
            <h3 className="text-base font-bold whitespace-nowrap">
              JDIX Agents
            </h3>
          )}
        </MobileHeader>
        {/* Desktop Header - Only show when there are messages */}
        {/* 🔥 重构：使用 ChatHeader 组件替代内联代码 */}
        {/* 面包屑导航：项目名/标题 或 Shared/标题 */}
        {showHeader && (
          <div className="hidden lg:flex items-center border-b border-claude-border pr-16 sm:pr-0 overflow-x-auto scrollbar-hide bg-white min-h-[44px] sm:min-h-0">
            {/* File list toggle button - only show when there's a project */}
            {actualProjectId && !showFileList && (
              <button
                onClick={() => setShowFileList(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 ml-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                title="View project files"
              >
                <FolderOpen size={16} />
                <span>Files</span>
              </button>
            )}
            <div className="flex-1">
              <ChatHeader
                title={displayConversation?.title || 'New Chat'}
                projectId={actualProjectId}
                projectName={actualProjectName}
                projectRole={actualProjectRole}
                onProjectClick={onProjectClick}
                isSharedRecordView={isSharedRecordView}
                isSkillConversation={isCurrentSkillConversation}
                onBackToShared={onBackToShared}
                conversationId={currentConversationId}
              />
            </div>
          </div>
        )}
        {sharedLoadError && isSharedRecordView ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="max-w-md w-full bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
              <div className="text-red-800 font-medium mb-2">
                Failed to load
              </div>
              <div className="text-red-700 text-sm break-words">
                {sharedLoadError}
              </div>
            </div>
          </div>
        ) : displayConversation?.messages &&
          displayConversation.messages.length > 0 ? (
          // Layout with messages - input at bottom
          <>
            {/* Messages */}
            <ChatMessagesView
              displayConversation={displayConversation}
              isCurrentGroupChat={isCurrentGroupChat}
              useMessagesArray={isCurrentSkillConversation}
              isReadOnlyMode={isReadOnlyMode}
              canChangeVersionInThisView={canChangeVersionInThisView}
              isSharedRecordView={isSharedRecordView}
              selectedAgent={selectedAgent}
              scrollContainerRef={scrollContainerRef}
              messagesEndRef={messagesEndRef}
              handleSourceClick={handleSourceClick}
              handleDelete={handleDelete}
              handleRetry={handleRetry}
              handleEditSave={handleEditSave}
              handleChangeChoiceForTree={handleChangeChoiceForTree}
              handleChangeChoiceForSharedTree={handleChangeChoiceForSharedTree}
              handleChangeChoice={handleChangeChoice}
              handleChangeChoiceForSharedMessages={
                handleChangeChoiceForSharedMessages
              }
              handleStopRequest={handleStopRequest}
              handleCancelMessage={handleCancelMessage}
              setReplyToMessage={setReplyToMessage}
            />
            {/* Chat Input at bottom - disabled if sharing */}
            {!isReadOnlyMode && (
              <div className="flex-shrink-0 relative pb-[calc(env(safe-area-inset-bottom)+0.5rem)]">
                {/* 滚动到底部悬浮按钮 */}
                {showScrollButton && (
                  <button
                    onClick={scrollToBottom}
                    className="absolute left-1/2 -translate-x-1/2 -top-6 w-10 h-10 bg-white border border-gray-300 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group hover:bg-gray-50 z-10"
                    title="Scroll to bottom"
                  >
                    <svg
                      className="w-5 h-5 text-gray-600 group-hover:text-gray-800"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      />
                    </svg>
                  </button>
                )}
                <div className="max-w-4xl mx-auto px-2 py-2">
                  <ChatInput
                    onSendMessage={handleSendMessage}
                    onStopRequest={handleStopRequest}
                    onNewConversation={handleNewConversation}
                    // 🔥 修改：只有切换会话或队列满（>=10）时才禁用，等待 message_init 时不禁用
                    disabled={
                      // 🔥 修复：new chat 页面（无 currentConversationId）永不禁用
                      currentConversationId
                        ? isSwitchingConversation ||
                          pendingTasks.filter(
                            t => t.conversationId === currentConversationId
                          ).length >= 10
                        : false
                    }
                    isLoading={currentIsLoading}
                    taskError={currentTaskError}
                    onClearTaskError={handleClearTaskError}
                    placeholder={
                      currentIsLoading
                        ? t('chat.generatingResponse') ||
                          'Generating response...'
                        : t('chat.replyPlaceholder') ||
                          `Message ${branding.shortName}`
                    }
                    projectId={actualProjectId}
                    projectName={actualProjectName}
                    agentConfig={selectedAgent?.config}
                    conversationId={currentConversationId}
                    conversationAgent={currentConversationAgent}
                    onProjectClick={onProjectClick}
                    isGroupChat={isCurrentGroupChat}
                    replyToMessage={replyToMessage}
                    onClearReply={() => setReplyToMessage(null)}
                    onStopGroupChat={
                      isCurrentGroupChat ? handleStopGroupChat : undefined
                    }
                    isStarred={isConversationStarred}
                    onStarredChange={setIsConversationStarred}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          // Welcome layout - input in center
          <ChatWelcomeView
            selectedAgent={selectedAgent}
            isReadOnlyMode={isReadOnlyMode}
            isSwitchingConversation={isSwitchingConversation}
            currentIsLoading={currentIsLoading}
            actualProjectId={actualProjectId}
            actualProjectName={actualProjectName}
            currentConversationId={currentConversationId}
            currentConversationAgent={currentConversationAgent}
            isCurrentGroupChat={isCurrentGroupChat}
            replyToMessage={replyToMessage}
            handleSendMessage={handleSendMessage}
            handleStopRequest={handleStopRequest}
            handleNewConversation={handleNewConversation}
            handleStopGroupChat={handleStopGroupChat}
            onProjectClick={onProjectClick}
            setReplyToMessage={setReplyToMessage}
            isWaitingForMessageInit={isWaitingForMessageInit}
            currentTaskError={currentTaskError}
            onClearTaskError={handleClearTaskError}
          />
        )}

        {/* Shared back button at bottom */}
        {isSharedRecordView && onBackToShared && (
          <div className="flex-shrink-0 border-t border-claude-border bg-claude-bg px-4 sm:px-6 py-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]">
            <div className="max-w-4xl mx-auto flex justify-center">
              <button
                type="button"
                onClick={onBackToShared}
                className="text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors inline-flex items-center gap-2"
              >
                <ChevronLeft size={16} />
                Back to Shared
              </button>
            </div>
          </div>
        )}

        {/* 🔥 移除：不再需要等待任务列表，消息在 message_init 时直接渲染并显示状态标签 */}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteConfirmDialog.isOpen}
          title="Delete Session"
          message="Deleting this session will remove all subsequent sessions. Do you want to continue?"
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirmDialog({ isOpen: false })}
        />
      </div>

      {/* 🔥 文件预览（单例模式 - 从 store 读取状态）
          - PC端自动使用分屏模式 (split)
          - 移动端自动使用弹窗模式 (overlay)
          - 点击其他文件只替换内容，不重新打开 */}
      {filePreview && <FilePreview readOnly={isReadOnlyMode} />}

      {/* Group chat member list - right sidebar */}
      {isCurrentGroupChat && currentConversationId && (
        <GroupMemberList
          conversationId={currentConversationId}
          isGroupChat={isCurrentGroupChat}
          projectId={actualProjectId}
        />
      )}
    </div>
  );
}
